import { cn } from '@/lib/core/utils/cn';
import { useApp } from '@/lib/core/providers/appProvider';
import { ImagesToImageMap } from '@/lib/core/utils/image';

/**
 * Display width of the loading mark, chosen to match the footprint of the word mark it replaced so
 * the surrounding layout (gap-8 stack, caption, spinner) keeps the proportions from the design.
 * The height follows the image's own aspect ratio.
 */
const LOGO_WIDTH_PX = 280;

function LogoAccent({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 38.001 13.2783"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('block shrink-0 overflow-visible', className)}
      style={{ width: '38.001px', height: '13.278px' }}
    >
      <path
        d="M38.001 13.2783H27.8672L18.499 5H18.3496L10.1338 13.2783H0L11.999 0L14.999 2.5L18.499 0L21.999 2.5L24.499 0L38.001 13.2783Z"
        fill="currentColor"
      />
    </svg>
  );
}

function LogoText() {
  return (
    <div className="inline-grid grid-cols-[max-content] grid-rows-[max-content] place-items-start leading-[0]">
      <span className="font-pp-neue-montreal col-start-1 row-start-1 mt-[2.5px] text-[67.521px] leading-[67.521px] font-semibold tracking-[1px] whitespace-nowrap">
        JNcQUOI
      </span>
      <LogoAccent className="col-start-1 row-start-1 ml-[85.67px]" />
    </div>
  );
}

/**
 * The loading mark: the logo authored in Umbraco under global settings (`appSettings.logo`).
 *
 * Falls back to the typeset word mark when the setting is empty — the two layers below are the
 * whole loading screen, so an unset logo would otherwise leave it blank.
 */
function LogoMark() {
  const { appSettings } = useApp();
  const logo = ImagesToImageMap(appSettings?.properties?.logo);

  if (!logo?.src) return <LogoText />;

  return (
    <img
      src={logo.src}
      alt={logo.alt ?? ''}
      className="block max-w-full"
      style={{ width: LOGO_WIDTH_PX, height: 'auto' }}
    />
  );
}

interface LogoLoadingProps {
  className?: string;
  progress?: number;
  /** Optional two-line caption shown below the logo mark, e.g. "Finding your driver". */
  caption?: { title: string; subtitle?: string };
  /** Optional slot pinned to the top of the screen (e.g. a small page header), above the centered logo. */
  header?: React.ReactNode;
  /** Optional slot pinned to the bottom of the screen (e.g. action buttons). */
  footer?: React.ReactNode;
  /**
   * Optional slot rendered directly beneath the logo mark, inside the centered stack — for a
   * spinner or other indicator that has to sit with the logo rather than at the screen edge.
   * Appears above `caption` when both are given.
   */
  belowLogo?: React.ReactNode;
}

export function LogoLoading({ className, progress, caption, header, footer, belowLogo }: LogoLoadingProps) {
  const isControlled = progress !== undefined;
  const rightClip = isControlled ? (1 - Math.min(1, Math.max(0, progress))) * 100 : undefined;

  return (
    <div
      className={cn(
        'fixed top-0 left-0 flex h-screen w-full flex-col items-center justify-center',
        className
      )}
    >
      {header && <div className="absolute inset-x-0 top-0">{header}</div>}
      {footer && <div className="absolute inset-x-0 bottom-0">{footer}</div>}
      <div className="flex flex-col items-center gap-8">
        <div className="relative">
          <div className="text-jncquoi-neutral-1000 opacity-30">
            <LogoMark />
          </div>
          <div
            className={cn(
              'text-jncquoi-neutral-1000 absolute inset-0',
              !isControlled && 'animate-logo-reveal'
            )}
            style={
              isControlled
                ? { clipPath: `inset(0 ${rightClip}% 0 0)`, transition: 'clip-path 0.12s ease-out' }
                : undefined
            }
          >
            <LogoMark />
          </div>
        </div>
        {belowLogo}
        {caption && (
          <div className="flex flex-col items-center gap-2 px-4 text-center">
            <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[16px] tracking-[0.25px]">
              {caption.title}
            </p>
            {caption.subtitle && (
              <p className="font-pp-neue-montreal text-[16px] tracking-[0.5px] text-[#a9a9a9]">
                {caption.subtitle}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
